import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, Icon } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PanesHeader from 'modules/core/components/PanesHeader'
import type { PanesHeaderActionProps } from 'modules/core/components/PanesHeader'
import PanesPadForm from 'modules/core/components/PanesPadForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import FieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/FieldsForm'
import { getForm } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/getForm'
import type ResourceField from 'modules/custom_resources/store/models/customFields/ResourceField'
import type { ICustomFieldValue } from 'modules/custom_resources/store/models/customFields/Value'
import type CustomFieldValue from 'modules/custom_resources/store/models/customFields/Value'
import type Employee from 'modules/employees/store/models/Employee'

interface Props {
  padFormDirection: 'vertical' | 'horizontal'
  boxed: boolean
  employee?: Employee
  fieldValues: CustomFieldValue[]
  resourceFields: ResourceField[]
  createValue: (data: Record<string, unknown>) => Promise<ICustomFieldValue>
  title?: string
  description?: string
  icon?: React.ComponentProps<typeof Icon.Large>['icon']
  readonly?: boolean
  headerAction?: PanesHeaderActionProps | null | undefined
  customFieldsOnly?: boolean
  editableFieldsOnly?: boolean
  contractVersions: ContractVersions
}

const CustomFieldsForm = ({
  padFormDirection,
  boxed,
  employee,
  fieldValues,
  resourceFields,
  createValue,
  title,
  description,
  icon,
  readonly,
  headerAction,
  customFieldsOnly,
  editableFieldsOnly,
  contractVersions,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { forms, hasCustomFieldTypes } = getForm(
    resourceFields,
    fieldValues,
    contractVersions,
    employee
  )

  if (!hasCustomFieldTypes || forms.length === 0) return null

  const createNewField = () => {
    navigate(navigation.settings.customizations.fullPath())
  }

  if (customFieldsOnly) {
    return (
      <FieldsForm
        employee={employee}
        fieldValues={fieldValues}
        resourceFields={resourceFields}
        createValue={createValue}
        readonly={readonly}
        editableFieldsOnly={editableFieldsOnly}
        contractVersions={contractVersions}
      />
    )
  }

  return (
    <PanesPadForm direction={padFormDirection} boxed={boxed}>
      <PanesHeader
        illustration={icon || 'input-field'}
        title={title || i18n.t('settings.custom_fields.title')}
        description={description || i18n.t('settings.custom_fields.description')}
        action={headerAction}
      />

      {forms.length === 0 && (
        <Button onClick={createNewField} type="primary">
          {i18n.t('employee_form.create_custom_field')}
        </Button>
      )}

      <FieldsForm
        employee={employee}
        fieldValues={fieldValues}
        resourceFields={resourceFields}
        createValue={createValue}
        readonly={readonly}
        contractVersions={contractVersions}
      />
    </PanesPadForm>
  )
}

export default observer(CustomFieldsForm)
